import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Drop, Plus, Trash, Warning, FloppyDisk } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GLYCEMIA_TYPES, GLYCEMIA_TYPE_LABELS, limitFor, isOverLimit, computeWeeklyWithinRange } from '@/lib/glycemia';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GlicemiaTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [data, setData] = useState({ records: [], limits: {}, enabled: false });
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [limits, setLimits] = useState({});
  const [savingCfg, setSavingCfg] = useState(false);
  const now = new Date();
  const defForm = () => ({ date: now.toISOString().split('T')[0], time: now.toTimeString().slice(0, 5), reading_type: 'jejum', value: '', notes: '' });
  const [form, setForm] = useState(defForm());

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/patients/${patientId}/glycemia`);
      setData(data || { records: [], limits: {}, enabled: false });
      setEnabled(!!data.enabled);
      setLimits(data.limits || {});
    } catch { /* noop */ } finally { setLoading(false); }
  }, [patientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const saveSettings = async () => {
    setSavingCfg(true);
    try {
      const cleanLimits = {};
      GLYCEMIA_TYPES.forEach(t => { const v = limits[t]; if (v !== '' && v !== null && v !== undefined) cleanLimits[t] = parseFloat(v); });
      await axios.put(`${API}/api/patients/${patientId}/glycemia-settings`, { enabled, limits: cleanLimits });
      toast.success('Definições guardadas'); fetchData();
    } catch { toast.error('Erro ao guardar'); } finally { setSavingCfg(false); }
  };

  const add = async () => {
    if (form.value === '') { toast.error('Indica o valor'); return; }
    try { await axios.post(`${API}/api/patients/${patientId}/glycemia`, { ...form, value: parseFloat(form.value) }); toast.success('Leitura guardada'); setForm(defForm()); fetchData(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao guardar'); }
  };
  const del = async (id) => {
    const rec = data.records.find(r => r.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a leitura de glicemia de ${rec?.date || ''}${rec?.value != null ? ` (${rec.value} mg/dL)` : ''}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/patients/${patientId}/glycemia/${id}`); toast.success('Eliminado'); fetchData(); } catch { /* noop */ }
  };

  const weekly = computeWeeklyWithinRange(data.records, data.limits);

  if (loading) return <p className="text-center text-brand-muted py-8 text-sm" data-testid="admin-glicemia-tab">A carregar...</p>;

  return (
    <div className="space-y-4" data-testid="admin-glicemia-tab">
      {/* Settings */}
      <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="glycemia-settings">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-sm text-brand-text flex items-center gap-2"><Drop size={16} /> Módulo de glicemia</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="rounded" data-testid="glycemia-enabled-toggle" />
            <span className="text-xs text-brand-muted">{enabled ? 'Ativo' : 'Inativo'}</span>
          </label>
        </div>
        <p className="text-[11px] text-brand-muted mb-3">Define os limites máximos (zona segura) por tipo de medição. Deixa em branco os tipos sem limite — esses ficam fora dos alertas e do cálculo de "% dentro do intervalo".</p>
        <div className="grid grid-cols-2 gap-2">
          {GLYCEMIA_TYPES.map(t => (
            <div key={t}>
              <Label className="text-xs text-brand-muted mb-1 block">{GLYCEMIA_TYPE_LABELS[t]} (máx. mg/dL)</Label>
              <Input type="number" step="1" value={limits[t] ?? ''} onChange={e => setLimits({ ...limits, [t]: e.target.value })} placeholder="—" className="rounded-xl border-brand-border" data-testid={`glycemia-limit-${t}`} />
            </div>
          ))}
        </div>
        <button onClick={saveSettings} disabled={savingCfg} className="mt-3 bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1 disabled:opacity-50" data-testid="save-glycemia-settings-btn"><FloppyDisk size={14} /> Guardar definições</button>
      </div>

      {/* Add reading */}
      {enabled && (
        <div className="bg-white border border-brand-border rounded-2xl p-4">
          <h3 className="font-heading text-sm text-brand-text mb-3">Registar leitura</h3>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="admin-glycemia-date" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Hora</Label><Input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} className="rounded-xl border-brand-border" data-testid="admin-glycemia-time" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Momento</Label>
              <select value={form.reading_type} onChange={e => setForm({ ...form, reading_type: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="admin-glycemia-type">
                {GLYCEMIA_TYPES.map(t => <option key={t} value={t}>{GLYCEMIA_TYPE_LABELS[t]}</option>)}
              </select>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Valor (mg/dL)</Label><Input type="number" step="1" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="Ex: 95" className="rounded-xl border-brand-border" data-testid="admin-glycemia-value" /></div>
          </div>
          <button onClick={add} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1" data-testid="admin-add-glycemia-btn"><Plus size={14} /> Adicionar</button>
        </div>
      )}

      {/* % within range */}
      {weekly.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="admin-glicemia-within-range">
          <h4 className="text-[10px] text-brand-muted uppercase tracking-wider mb-1">% de leituras dentro do intervalo</h4>
          <p className="text-[10px] text-brand-placeholder mb-2">Só conta leituras cujo tipo tem limite definido</p>
          <div className="space-y-1.5">
            {weekly.slice().reverse().map(b => (
              <div key={b.week} className="flex justify-between text-xs" data-testid={`admin-glicemia-week-${b.week}`}>
                <span className="text-brand-text">Semana {b.label}</span>
                <span className={`font-semibold ${b.pct >= 70 ? 'text-brand-green' : b.pct >= 40 ? 'text-brand-gold-hover' : 'text-red-500'}`}>{b.pct}% <span className="text-[10px] font-normal text-brand-muted">({b.within}/{b.total})</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Records */}
      {data.records.length === 0 ? (
        <p className="text-center text-brand-muted py-8 text-sm">Sem leituras registadas.</p>
      ) : (
        <div className="space-y-2">
          {data.records.map(r => { const over = isOverLimit(r, data.limits); return (
            <div key={r.id} className={`border rounded-xl p-3 flex items-center justify-between text-sm ${over ? 'bg-red-50 border-red-200' : 'bg-white border-brand-border'}`} data-testid={`admin-glycemia-record-${r.id}`}>
              <div>
                <span className="text-xs text-brand-muted">{fmtDatePt(r.date)} · {r.time}</span>
                <p className="text-xs text-brand-text mt-0.5">{GLYCEMIA_TYPE_LABELS[r.reading_type] || r.reading_type}{limitFor(data.limits, r.reading_type) !== null ? ` · limite ${limitFor(data.limits, r.reading_type)}` : ''}</p>
              </div>
              <div className="flex items-center gap-3">
                {over && <span className="flex items-center gap-1 text-[10px] text-red-500 font-medium"><Warning size={13} weight="fill" /> Acima</span>}
                <strong className={over ? 'text-red-500' : 'text-brand-text'}>{r.value} mg/dL</strong>
                <button onClick={() => del(r.id)} className="text-brand-placeholder hover:text-red-500"><Trash size={13} /></button>
              </div>
            </div>
          ); })}
        </div>
      )}
    </div>
  );
}
